import { FunctionComponent } from "react";
import ListItem from "../ListItem/ListItem";
import { Heading } from "../Text/Heading";
import { addToScore, createScore, Score } from "../../../lib/types/form/submission/score";
import { PageId } from "../../../lib/types/ids";
import { UnencryptedAnswers } from "../../../lib/types/form/submission/answer";
import { resolveStaticPath, UnresolvedStaticPath } from "../../../lib/types/static-path";
import { isPrintable } from "../../../lib/utils/printable";

export interface OptInSummaryProps {
  id: string;
  questionId: string;
  heading: string;
  icon: UnresolvedStaticPath;
  iconLabel: string;
  answers: UnencryptedAnswers;
  resolveExpression: (expression: string) => unknown;
  score: { [key: PageId]: Score };
  scoreExpression: string | undefined;
  note: string;
  list: {
    id: string;
    icon: UnresolvedStaticPath;
    label: string;
    ref?: PageId;
  }[];
}

const OptInSummary: FunctionComponent<OptInSummaryProps> = ({
  icon,
  iconLabel,
  heading,
  list,
  answers,
  score,
  resolveExpression,
  scoreExpression,
  note,
}) => {
  const listItems = list.map((listItem, index) => {
    let refLabel;
    if (listItem.ref) {
      const resolved: unknown = resolveExpression(listItem.ref);
      if (resolved) {
        refLabel =
          Array.isArray(resolved) ? resolved.join(", ")
            : isPrintable(resolved) ? `${resolved}`
              : "";
      } else {
        const answer = answers[listItem.ref];
        if (answer?.data) {
          let stringArray = [];
          if (answer.type === "input") {
            stringArray = Object.values(answer.data).map(
              (el) => el?.value || "––"
            );
          } else {
            stringArray = Object.values(answer.data).map(
              (el) => el?.label || "––"
            );
          }
          refLabel = stringArray.join(", ");
        }
      }
    }
    return (
      <ListItem
        icon={listItem.icon}
        key={listItem.id}
        last={index === list.length - 1}
      >
        {listItem.label}
        {refLabel}
      </ListItem>
    );
  });
  let displayedScore: Score | undefined;
  if (scoreExpression) {
    const exprResult = Score.decode(resolveExpression(scoreExpression));
    if (exprResult._tag === "Right") {
      displayedScore = exprResult.right;
    }
  }
  if (displayedScore !== 0 && !displayedScore) {
    displayedScore = createScore(0);
    for (const id of Object.keys(score)) {
      const scoreValue = score[id];
      if (scoreValue) {
        displayedScore = addToScore(displayedScore, scoreValue);
      }
    }
  }

  if (note && note.startsWith("/)")) {
    const resolved = resolveExpression(note.substring(2));
    if (typeof resolved === "string") {
      note = resolved;
    }
  }
  return (
    <div
      className={
        "flex flex-col p-8 lg:p-10 border rounded-single-choice lg:max-w-md  w-full text-left border-gray-300"
      }
    >
      <div className="flex mb-2 items-center">
        {icon ? <img src={resolveStaticPath(icon)} className="mr-3 component-header-icon" /> : null}
        <p>{iconLabel}</p>
      </div>
      <Heading level={3} className="mb-8">
        {heading}
      </Heading>
      <ul>{listItems}</ul>
      <div className="flex items-baseline h-full label-container mt-8" data-type="summary-price">
        <Heading level={2} className="text-3xl text-primary font-bold label">
          {displayedScore.toFixed(2).replace(".", ",")}€
          <span className="text-gray-500 text-xl font-normal label-note"> {note}</span>
        </Heading>
      </div>
    </div>
  );
};

export default OptInSummary;
